import React from 'react';
import { useMutation } from 'react-query';
import * as yup from 'yup';

import {
  Modal,
  ModalClose,
  ModalCloseButton,
  ModalContent,
  ModalTitle,
} from '@raptormaps/modal';
import { useToast } from '@raptormaps/toast';
import { Row, Stack } from '@raptormaps/layout';
import { Text } from '@raptormaps/text';
import { RadioGroup } from '@raptormaps/radio-group';
import { Button } from '@raptormaps/button';

import { getUpdateOrgAccessToFarmMutation } from '../../../api/services/farm-orgs';

interface UpdateOrgAccessToFarmProps {
  solarFarmOrgId: number;
  solarFarmId: number;
  orgId: number;
  orgName: string;
  permissionId: number;
  canEdit: boolean;
  onClose: () => void;
}

export const UpdateOrgAccessToFarm = ({
  solarFarmOrgId,
  solarFarmId,
  orgId,
  orgName,
  permissionId,
  canEdit,
  onClose,
}: UpdateOrgAccessToFarmProps) => {
  const toast = useToast();

  const { mutateAsync: updateOrgAccessToFarm, isLoading: isSubmitting } =
    useMutation(
      getUpdateOrgAccessToFarmMutation(
        solarFarmId,
        solarFarmOrgId,
        orgId,
        permissionId,
      ),
    );

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const editAccessFormEntry = formData.get('edit-access');

    try {
      const editAccessFormValue = yup
        .string()
        .oneOf(['yes', 'no'])
        .defined()
        .validateSync(editAccessFormEntry);

      toast.promise(
        updateOrgAccessToFarm({ canEdit: editAccessFormValue === 'yes' }).then(
          () => onClose(),
        ),
        {
          loading: 'Updating org access to farm...',
          error: 'Unable to update org access to the farm. Please try again.',
          success: 'Org access to farm has been updated!',
        },
      );
    } catch {
      toast.error('Invalid value for "Edit Access."');
    }
  };

  return (
    <Modal open onClose={onClose}>
      <ModalContent>
        <ModalCloseButton />
        <Stack
          as="form"
          onSubmit={handleSubmit}
          gutter="xl"
          className="remove_label_margin"
          style={{ minWidth: 350 }}
        >
          <ModalTitle>Edit Permissions</ModalTitle>
          <Text>Edit the permissions {orgName} has for this site.</Text>
          <Stack gutter="xs">
            <Text as="label" htmlFor="edit-access">
              Edit Access
            </Text>
            <RadioGroup
              id="edit-access"
              name="edit-access"
              disabled={isSubmitting}
              defaultValue={canEdit ? 'yes' : 'no'}
              items={[
                { label: 'On', value: 'yes' },
                { label: 'Off', value: 'no' },
              ]}
            />
          </Stack>
          <Row>
            <ModalClose asChild>
              <Button variant="secondary" flex disabled={isSubmitting}>
                Cancel
              </Button>
            </ModalClose>
            <Button type="submit" flex disabled={isSubmitting}>
              Apply Changes
            </Button>
          </Row>
        </Stack>
      </ModalContent>
    </Modal>
  );
};
